/**
 * balance-colonisation.ts — colonisation balance constants (MFix3.3).
 *
 * Mirrors the structure of `balance-bombardment.ts` and `balance-structures.ts`:
 * tuning knobs live here, the `applyColonise` command in `commands.ts` and
 * the auto-colonise hook in `advance.ts` (MFix3.4) read from this file.
 */

import type { PlanetSize } from '../state/types/index';
import { planetSimRadiusForSize } from './balance-bombardment';

/**
 * Distance a Coloniser may be from the planet's centre and still land.
 * Derived from `planetSimRadiusForSize(planet.size) + approachMargin` so
 * larger planets are easier to reach without a pixel-perfect approach.
 *
 * approachMargin is one ship-sprite-width worth of slack at max zoom
 * (matches MFix1.2's SHIP_MIN_SEPARATION lower bound). Tuning is a
 * v3M10 balance pass concern.
 *
 * v3M10.1: migrated to state.balanceData.colonisation.approachMargin.
 */

import type { BalanceData } from '../state/types/index';

/** Sim-coordinate range within which a Coloniser may land on the planet. */
export function colonisationRangeFor(size: PlanetSize, balance: BalanceData): number {
  return planetSimRadiusForSize(size) + balance.colonisation.approachMargin;
}

/**
 * Initial Loyalty for a freshly-colonised planet (PLANETS §2.7 lock —
 * "Loyalty = 100 for every planet the moment a player owns it").
 * Re-colonised planets after depopulation reset to this value too.
 *
 * v3M10.1: migrated to state.balanceData.colonisation.initialLoyalty.
 */

/**
 * The specialAbility string in a ship template that identifies a
 * Coloniser-class ship. Authored in `docs/Ships.xlsx` — kept here as a
 * named constant so the colonise command and any future UI affordances
 * (Build menu filtering, hover hints) reference the same canonical string.
 */
export const COLONISER_SPECIAL_ABILITY = 'Colonise planet';
